// Read headers before decoding: a tiny compressed file can describe a huge bitmap.
// Reads at most 30 bytes at a time and scans at most 256 JPEG segments.
export async function imageDimensions(
  file: Blob,
): Promise<{ w: number; h: number }> {
  const read = async (offset: number, size: number) =>
    new DataView(await file.slice(offset, offset + size).arrayBuffer());
  const head = await read(0, 30);
  let w = 0,
    h = 0;
  if (
    head.byteLength >= 24 &&
    head.getUint32(0) === 0x89504e47 &&
    head.getUint32(4) === 0x0d0a1a0a &&
    head.getUint32(12) === 0x49484452
  ) {
    w = head.getUint32(16);
    h = head.getUint32(20);
  } else if (
    head.byteLength >= 30 &&
    head.getUint32(0) === 0x52494646 &&
    head.getUint32(8) === 0x57454250
  ) {
    const type = head.getUint32(12);
    const uint24 = (offset: number) =>
      head.getUint8(offset) |
      (head.getUint8(offset + 1) << 8) |
      (head.getUint8(offset + 2) << 16);
    if (type === 0x56503858) {
      // VP8X
      w = uint24(24) + 1;
      h = uint24(27) + 1;
    } else if (type === 0x5650384c && head.getUint8(20) === 0x2f) {
      // VP8L
      const bits = head.getUint32(21, true);
      w = (bits & 0x3fff) + 1;
      h = ((bits >>> 14) & 0x3fff) + 1;
    } else if (type === 0x56503820 && uint24(23) === 0x2a019d) {
      // VP8
      w = head.getUint16(26, true) & 0x3fff;
      h = head.getUint16(28, true) & 0x3fff;
    }
  } else if (head.byteLength >= 10 && head.getUint32(0) === 0x47494638) {
    w = head.getUint16(6, true);
    h = head.getUint16(8, true);
  } else if (
    head.byteLength >= 26 &&
    head.getUint16(0) === 0x424d &&
    head.getUint32(14, true) >= 40
  ) {
    w = head.getInt32(18, true);
    h = Math.abs(head.getInt32(22, true));
  } else if (head.byteLength >= 16 && head.getUint32(4) === 0x66747970) {
    // ISO BMFF still images (AVIF/HEIF) expose bounded dimensions in an ispe box.
    // Read only metadata bytes; unsupported browser codecs still fall back to a card.
    const bytes = new Uint8Array(
        await file.slice(0, Math.min(file.size, 1024 * 1024)).arrayBuffer(),
      ),
      text = new TextDecoder("latin1").decode(bytes.subarray(8, 32));
    if (/avif|avis|mif1|msf1|heic|heix|hevc|hevx/.test(text))
      for (let offset = 4; offset + 16 <= bytes.length; offset++)
        if (
          bytes[offset] === 0x69 &&
          bytes[offset + 1] === 0x73 &&
          bytes[offset + 2] === 0x70 &&
          bytes[offset + 3] === 0x65
        ) {
          const view = new DataView(
            bytes.buffer,
            bytes.byteOffset,
            bytes.byteLength,
          );
          w = view.getUint32(offset + 8);
          h = view.getUint32(offset + 12);
          break;
        }
  } else if (head.byteLength >= 2 && head.getUint16(0) === 0xffd8) {
    let offset = 2;
    for (let count = 0; count < 256 && offset + 4 <= file.size; count++) {
      const segment = await read(offset, 9);
      if (segment.getUint8(0) !== 0xff) break;
      const marker = segment.getUint8(1);
      if (marker === 0xff) {
        offset++;
        continue;
      }
      if (marker === 0xda || marker === 0xd9) break;
      const length = segment.getUint16(2);
      if (length < 2 || offset + 2 + length > file.size) break;
      if (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        ![0xc4, 0xc8, 0xcc].includes(marker) &&
        segment.byteLength >= 9
      ) {
        h = segment.getUint16(5);
        w = segment.getUint16(7);
        break;
      }
      offset += 2 + length;
    }
  }
  if (w <= 0 || h <= 0)
    throw new Error("Cannot read this image or its dimensions.");
  if (w * h > 40_000_000)
    throw new Error("Use an image with fewer than 40 million pixels.");
  return { w, h };
}
